import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid2 as Grid,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import IntlMessages from "../../components/IntlMessages/IntlMessages";
import { PageHeader } from "../../components/PageHeader";
import { TextField } from "../../components/TextField";
import { useUsersActions } from "../../contexts/users/UseUsersActions";
import { useUsersContext } from "../../contexts/users/UsersContext";
import {
  UserCreateData,
  Role,
  UserUpdateData,
  UserRole,
} from "../../core/models/User";
import { notify } from "../../libs/observables/notification";
import { UNSelect } from "un-react-ui-components";
import { useIntl } from "react-intl";
import { useAuthContext } from "../../contexts/authentication/AuthenticationContext";
import { useRequestsActions } from "../../contexts/requests/UseRequestsActions";
import { useRequestsContext } from "../../contexts/requests/RequestsContext";
import useAuthenticationActions from "../../contexts/authentication/UseAuthenticationActions";

const EMAIL_VALIDATOR = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

const UserNewEdit = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { userInfo } = useAuthContext();
  const { dispatchLogout } = useAuthenticationActions();
  const USER_ROLES = Object.values(Role).map((role) => {
    return {
      label: intl.formatMessage({ id: `user.role.${role}` }),
      value: role,
    };
  });
  const params = useParams<{ id: string }>();
  const { selectedUser, usersError } = useUsersContext();
  const {
    dispatchGetUser,
    dispatchCreateUser,
    dispatchUpdateUser,
    dispatchDeleteUser,
    dispatchCleanUsersContext,
  } = useUsersActions();
  const { dispatchGetRequests, dispatchCleanRequestsContext } =
    useRequestsActions();
  const { requestsResponseData } = useRequestsContext();

  const requests = useMemo(() => {
    if (!requestsResponseData) return [];

    return Object.values(requestsResponseData.data).map((request) => ({
      label: request.title,
      value: request.id,
    }));
  }, [requestsResponseData]);

  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [role, setRole] = useState<Role>();
  const [accessRequest, setAccessRequest] = useState<Array<string>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const formNotChanged =
    !!params.id &&
    !!selectedUser &&
    selectedUser.firstName === firstName &&
    selectedUser.lastName === lastName &&
    selectedUser.email === email &&
    !!role &&
    selectedUser.integration_configurations === accessRequest &&
    selectedUser?.permissionRole === role;

  const disableSave =
    !firstName ||
    !lastName ||
    !email ||
    !email.match(EMAIL_VALIDATOR) ||
    !role ||
    !accessRequest ||
    formNotChanged;

  //#region Load and cleanup
  useEffect(() => {
    if (params.id) {
      dispatchGetUser(+params.id);
    }
    dispatchGetRequests();
    return () => {
      dispatchCleanRequestsContext();
      dispatchCleanUsersContext();
    };
  }, []);

  useEffect(() => {
    if (!params.id) {
      if (selectedUser && !usersError) {
        // onSendInvite();
        notify("success")({ message: "User created!" });
        navigate(-1);
      }
    } else {
      if (loading && selectedUser && !usersError) {
        notify("success")({ message: "User updated!" });
      } else if (selectedUser) {
        setFirstName(selectedUser.firstName);
        setLastName(selectedUser.lastName);
        setEmail(selectedUser.email);
        setRole(selectedUser.permissionRole);
        setAccessRequest(selectedUser.integration_configurations);
      }
    }

    setLoading(false);
  }, [selectedUser, usersError]);
  //#endregion

  //#region Page actions
  const onCancel = () => navigate(-1);

  const onSave = () => {
    if (!firstName) {
      notify("error")({ message: "User first name is required!" });
      return;
    }
    if (!lastName) {
      notify("error")({ message: "User last name is required!" });
      return;
    }
    if (!email || !email?.match(EMAIL_VALIDATOR)) {
      notify("error")({ message: "User email is required!" });
      return;
    }
    if (!role || !role.length) {
      notify("error")({ message: "User roles is required!" });
      return;
    }

    setLoading(true);
    const user: UserCreateData = {
      first_name: firstName,
      last_name: lastName,
      username: email,
      groups: [UserRole.Web],
      role: role,
      integration_configurations: accessRequest || [],
    };

    if (params.id) {
      const userModified: UserUpdateData = {
        id: +params.id,
        ...user,
      };
      dispatchUpdateUser(userModified);
      // If the current Admin user is modifying their own role to a non-Admin,
      // force logout to reflect permission changes immediately
      const isSameUser = userInfo?.email === userModified.username;
      const isCurrentUserAdmin = userInfo?.permissionRole === Role.Admin;
      const isModifiedRoleNotAdmin = userModified.role !== Role.Admin;
      if (isSameUser && isCurrentUserAdmin && isModifiedRoleNotAdmin) {
        setTimeout(() => {
          dispatchLogout();
        }, 200);
      }
    } else {
      dispatchCreateUser(user);
    }
  };
  //#endregion

  //#region User actions

  const onChangeRole = (role: Role) => {
    if (role === Role.Admin) {
      setAccessRequest([]);
    }
    setRole(role);
  };
  const onDeleteUser = () => {
    setLoading(true);
    dispatchDeleteUser(+params.id!)
      .then(() => {
        notify("success")({ message: "User deleted!" });
        navigate(-1);
      })
      .finally(() => setLoading(false));
  };
  //#endregion

  return (
    <>
      <Backdrop open={loading}>
        <CircularProgress data-testid="loader" />
      </Backdrop>
      <Box
        data-testid="user"
        className="min-w-full space-y-[1.875rem] flex-box"
      >
        <PageHeader
          titleTranslationKey={params.id ? "user.details" : "user.new"}
          tooltip="users"
          divider
          disableSave={disableSave}
          containerClass="flex-initial"
          saveTitle="save"
          onCancel={onCancel}
          onSave={onSave}
        />
        {/* Form */}
        <Grid container columnSpacing={4} className="flex-auto">
          {/* Left panel */}
          <Grid size={{ xs: 6 }} className="flex-box space-y-[1.875rem]">
            <Box className="flex-auto rounded-[10px] bg-gray-input p-[1.875rem]">
              <Box className="rounded-2xl bg-white border border-solid border-gray-background p-6 space-y-6">
                <TextField
                  id="user.firstName"
                  label="user.new.firstName"
                  placeholder="user.new.firstName.placeholder"
                  value={firstName}
                  onChange={setFirstName}
                />
                <TextField
                  id="user.lastName"
                  label="user.new.lastName"
                  placeholder="user.new.lastName.placeholder"
                  value={lastName}
                  onChange={setLastName}
                />
                <UNSelect
                  id="user.roles"
                  data-testid="user.roles"
                  labelTranslationKey={"user.roles"}
                  options={USER_ROLES}
                  value={role}
                  onChange={(role) => {
                    onChangeRole(role as Role);
                  }}
                />
                {userInfo?.permissionRole === Role.Admin &&
                role === Role.Business ? (
                  <UNSelect
                    id="user.access.requests"
                    data-testid="user.access.requests"
                    labelTranslationKey={"user.access.requests"}
                    options={requests}
                    value={accessRequest}
                    onChange={(access) =>
                      setAccessRequest(access as Array<string>)
                    }
                    multiple
                  />
                ) : null}
              </Box>
            </Box>
            {params.id ? (
              <Button
                data-testid="user.delete"
                variant="outlined"
                fullWidth
                onClick={onDeleteUser}
              >
                <IntlMessages id="user.delete" />
              </Button>
            ) : null}
          </Grid>
          {/* Right panel */}
          <Grid size={{ xs: 6 }}>
            {/* <Box className="flex items-end space-x-[0.625rem]"> */}
            <TextField
              id="user.email"
              label="user.email"
              placeholder="user.email.placeholder"
              // containerClass="flex-auto"
              value={email}
              onChange={setEmail}
            />
            {/* {params.id ? (
                <Button
                  data-testid="user.sendInvite"
                  disabled={!email?.match(EMAIL_VALIDATOR)}
                  className="py-4"
                  onClick={onSendInvite}
                >
                  <IntlMessages id="user.sendInvite" />
                </Button>
              ) : null} */}
            {/* </Box> */}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export { UserNewEdit };
